/**
 * Lazy analytics — keeps posthog-js (≈59KB gz, the whole vendor-analytics
 * chunk) OFF the critical boot path. Drop-in for the old
 * `import posthog from "posthog-js"` singleton: same `.capture` / `.identify`
 * signatures, so call sites only change their import line.
 *
 * posthog-js is dynamically imported at idle on first use and initialized
 * once; events fired before it loads are queued and flushed when ready.
 * No-op (and no dynamic import at all) when VITE_POSTHOG_KEY is unset.
 */
type Props = Record<string, unknown>;
type Method = "capture" | "identify";
type PostHog = typeof import("posthog-js").default;

let ph: PostHog | null = null;
let loading = false;
let failed = false;
// Bounded: analytics must never grow memory without limit when the chunk
// can't load (ad-blocker, offline). Oldest events drop first.
const MAX_QUEUE = 50;
const queue: Array<{ method: Method; args: unknown[] }> = [];

function flush() {
  if (!ph) return;
  while (queue.length) {
    const c = queue.shift()!;
    (ph[c.method] as (...a: unknown[]) => void)(...c.args);
  }
}

function ensureLoaded() {
  if (ph || loading || failed) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return; // analytics disabled — stays a no-op, chunk never loads
  loading = true;
  const start = () =>
    import("posthog-js")
      .then((mod) => {
        const posthog = mod.default;
        posthog.init(key, {
          api_host: "https://us.i.posthog.com",
          person_profiles: "identified_only",
          capture_pageview: false,
        });
        ph = posthog;
        flush();
      })
      .catch(() => {
        // Permanent for this page load: a failed chunk load (ad-blocker,
        // offline) would otherwise re-trigger the import on every later call.
        loading = false;
        failed = true;
        queue.length = 0;
        if (import.meta.env.DEV) console.warn("[analytics] posthog-js failed to load; analytics disabled for this session");
      });
  // Defer the import itself off the critical path; queued events wait for it.
  if (typeof requestIdleCallback !== "undefined") requestIdleCallback(start, { timeout: 3000 });
  else setTimeout(start, 1);
}

function call(method: Method, args: unknown[]) {
  if (!import.meta.env.VITE_POSTHOG_KEY || failed) return; // disabled → no-op, no queue growth
  if (ph) {
    (ph[method] as (...a: unknown[]) => void)(...args);
    return;
  }
  queue.push({ method, args });
  if (queue.length > MAX_QUEUE) {
    queue.shift();
    if (import.meta.env.DEV) console.warn(`[analytics] queue full (${MAX_QUEUE}); dropping oldest event`);
  }
  ensureLoaded();
}

const analytics = {
  capture: (event: string, props?: Props) => call("capture", [event, props]),
  identify: (id: string, props?: Props) => call("identify", [id, props]),
};

export default analytics;
